import { FC, useMemo } from 'react';
import { AssetsListGroup } from '@bts-web/data-layer/server';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { PillBar } from '../../../../PillBar/PillBar';
import { AssetSelectHookResult } from '../../utils';
import { SkeletonElement } from '../../../../loading/SkeletonElement/SkeletonElement';
import { css } from '@bts-web/utils-style-engine';

interface AssetsDrawerFilterHeaderProps {
  groups: AssetsListGroup[] | undefined;
  setGroups: (groups: AssetsListGroup[] | undefined) => void;
  assetGroups: AssetSelectHookResult['assetGroups'];
}

const translationKeyMap: Record<string, string> = {
  CRYPTO: 'dashboard_portfolio_crypto',
  STOCK: 'dashboard_portfolio_stocks',
  ETF: 'currency_filter_ETF',
  ETC: 'currency_filter_commodities',
  METAL: 'dashboard_portfolio_metals',
};

const AssetsDrawerFilterHeader: FC<AssetsDrawerFilterHeaderProps> = ({
  groups,
  setGroups,
  assetGroups,
}) => {
  const { t } = useClientTranslation();

  const options = useMemo(() => {
    const assetTypes: Record<
      string,
      { label: string; value: (AssetsListGroup | 'ALL_ASSETS')[] }
    > = {
      all_assets: {
        label: t('currency_filter_all_assets'),
        value: ['ALL_ASSETS'],
      },
    };

    assetGroups.forEach((group) => {
      const groupKey = group.key.toUpperCase();

      const translationKey = translationKeyMap[groupKey];

      const groupLabel = translationKey ? t(translationKey as any) : 'N/A';

      const groupValue = assetTypes[groupKey] || {
        label: groupLabel,
        value: group.value,
      };

      assetTypes[groupKey] = groupValue;
    });

    return assetTypes;
  }, [t, assetGroups]);

  const onChange = (value: string) => {
    const compositeValue = value.split(',');

    setGroups(
      compositeValue.length === 1 && compositeValue[0] === 'ALL_ASSETS'
        ? undefined
        : (compositeValue as AssetsListGroup[]),
    );
  };

  if (assetGroups.length === 0) {
    return (
      <div
        className={css({
          display: 'flex',
          gap: 'small',
          justifyContent: 'space-between',
        })}
      >
        {Array.from({ length: 3 }).map((_, index) => {
          return (
            <SkeletonElement
              key={index}
              borderRadius="5px"
              height="40px"
              minWidth="90px"
            />
          );
        })}
      </div>
    );
  }

  return (
    <PillBar
      items={Object.entries(options).map(([_, optionValue]) => ({
        label: optionValue.label,
        value: optionValue.value.join(','),
      }))}
      defaultValue={options.all_assets.value[0]}
      value={groups?.join(',') || 'ALL_ASSETS'}
      onValueChange={onChange}
    />
  );
};

export { AssetsDrawerFilterHeader };
